import React, { FC, useCallback, ReactNode } from 'react'
import { NavLink as RouterNavLink } from 'react-router-dom'
import classNames from 'classnames'
import omit from 'lodash/omit'
import { TIconComponent } from 'transfix-icons/types'
import {
  Box,
  createStyles,
  ListItem,
  ListItemIcon,
  ListItemProps,
  ListItemText,
  makeStyles,
  Tooltip,
  TooltipProps as MuiTooltipProps,
} from 'transfix-ui/core'
import { brandColors, getBrandColor } from 'transfix-ui/theme'

export interface INavListItemProps extends Omit<ListItemProps, 'button' | 'component' | 'classes'> {
  /**
   * Text for button
   * @example 'Home'
   */
  text?: ReactNode
  /**
   * @example '/home'
   */
  location?: string
  /**
   * @example Menu
   */
  IconEl?: TIconComponent | FC
  /**
   * Toggles whether to display only icon,
   * like when nav is not expanded
   * @default true
   */
  isTextVisible?: boolean
  /**
   * @default 'small'
   */
  size?: 'small' | 'large'
  /**
   * @example <Tag tagStyle='info' size='xsmall' />
   */
  tag?: ReactNode
  /**
   * @default 'dark'
   */
  variant?: 'light' | 'dark'
}

type TClassKey = 'root' | 'selected' | 'variantDark' | 'variantLight'

const useNavListItemStyles = makeStyles(theme =>
  createStyles<TClassKey, Pick<INavListItemProps, 'selected' | 'size' | 'isTextVisible'>>({
    root: {
      '&.MuiListItem-root': {
        margin: 0,
        borderRadius: theme.shape.borderRadius,
        paddingTop: ({ size }) => (size === 'large' ? theme.spacing(2) : 10.5),
        paddingBottom: ({ size }) => (size === 'large' ? theme.spacing(2) : 10.5),
        paddingLeft: ({ size }) => (size === 'large' ? theme.spacing(2) : theme.spacing(1.5)),
        paddingRight: ({ size }) => (size === 'large' ? theme.spacing(2) : theme.spacing(1.5)),
        marginBottom: theme.spacing(0.5),
        boxShadow: 'none',
        display: 'flex',
        justifyContent: ({ isTextVisible }) => (isTextVisible ? 'flex-start' : 'center'),
        color: brandColors.white,

        '&:focus': {
          boxShadow: 'none',
        },

        alignItems: 'flex-start',

        '& .MuiListItemIcon-root': {
          minWidth: 24,
          color: 'inherit',
        },
      },
    },
    variantDark: {
      '&.MuiListItem-root': {
        color: brandColors.white,

        '&:focus': {
          color: brandColors.white,
        },

        '&:hover': {
          backgroundColor: brandColors.skyBlue7,
          color: brandColors.coolGray1,
        },

        '&.Mui-selected': {
          backgroundColor: brandColors.skyBlue2,
          color: brandColors.skyBlue7,

          '&:focus': {
            color: brandColors.skyBlue7,
          },

          '&:hover': {
            backgroundColor: brandColors.skyBlue3,
            color: brandColors.skyBlue7,
          },
        },
      },
    },
    variantLight: {
      '&.MuiListItem-root': {
        color: brandColors.coolGray8,

        '&:focus': {
          color: brandColors.coolGray8,
        },

        '&:hover': {
          backgroundColor: brandColors.coolGray4,
          color: brandColors.coolGray8,
        },

        '&.Mui-selected': {
          backgroundColor: brandColors.skyBlue2,
          color: brandColors.skyBlue7,

          '&:focus': {
            color: brandColors.skyBlue7,
          },

          '&:hover': {
            backgroundColor: brandColors.skyBlue3,
            color: getBrandColor('skyBlue7').darken(0.8).hex(),
          },
        },
      },
    },
    selected: {},
  })
)

const useNavListItemTextWrapperStyles = makeStyles({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    flexGrow: 2,
  },
})

const useNavListItemTextStyles = makeStyles(theme => ({
  root: {
    '&.MuiListItemText-root': {
      whiteSpace: 'nowrap',
      overflowX: 'hidden',
      margin: 0,
      /**
       * Fixes a slight offset, and is slightly smaller on desktop
       * because of font size differences
       */
      marginTop: 2,

      [theme.breakpoints.up('md')]: {
        marginTop: 1,
      },

      minHeight: theme.spacing(3),
      display: 'flex',
      alignItems: 'center',
      paddingLeft: theme.spacing(1.25),
    },
  },
}))

const useNavListItemIconStyles = makeStyles(() =>
  createStyles({
    root: {
      '&.MuiNavListItemIcon-root': {
        minWidth: 24,
        /**
         * Allows color of icon to be defined by parent CSS style
         */
        color: 'inherit',
        maxHeight: 24,
      },
    },
  })
)

/**
 * Used in sidebar nav to direct to routes in an app
 */
export const NavListItem: FC<INavListItemProps> = ({
  IconEl,
  isTextVisible = true,
  location: locationProp = '',
  onClick: onClickProp,
  selected = false,
  size = 'small',
  tag: tagProp,
  text = '',
  variant = 'light',
  ...props
}) => {
  const navListItemClasses = useNavListItemStyles({
    selected,
    size,
    isTextVisible,
  })
  const navListItemClassName = classNames({
    [navListItemClasses.variantDark]: variant === 'dark',
    [navListItemClasses.variantLight]: variant === 'light',
  })

  const navListItemTextClasses = useNavListItemTextStyles()
  const navListItemIconClasses = useNavListItemIconStyles()
  const navListItemTextWrapperStyles = useNavListItemTextWrapperStyles()

  /**
   * Generates a component that can use both MUI and React Router's
   * props object to create a link
   */
  const LinkComponent = useCallback(
    ({ innerRef, ...linkProps }) => (
      <RouterNavLink
        ref={innerRef}
        to={locationProp}
        activeClassName='Mui-selected'
        {...linkProps}
      />
    ),
    [locationProp]
  )

  /**
   * If `location` has been passed as a prop, use that for React Router
   * Otherwise, just trigger `onClick` as a normal button
   */
  const Component = locationProp ? LinkComponent : 'li'

  const classes = omit(navListItemClasses, ['variantDark', 'variantLight'])

  return (
    <ListItem
      button
      component={Component}
      classes={classes}
      className={navListItemClassName}
      selected={selected}
      onClick={onClickProp}
      {...props}>
      {/**
       * Add Icon if optional prop is passed
       */}
      {IconEl && (
        <ListItemIcon classes={navListItemIconClasses}>
          <Box display='flex' alignItems='center' justifyContent='center'>
            <IconEl color='inherit' size='xlarge' />
          </Box>
        </ListItemIcon>
      )}
      {/**
       * When nav is not expanded, hide the text
       */}
      {isTextVisible && (
        <div className={navListItemTextWrapperStyles.root}>
          <ListItemText
            classes={navListItemTextClasses}
            primary={text}
            primaryTypographyProps={{
              variant: 'button',
              component: 'div',
            }}
          />
          {tagProp}
        </div>
      )}
    </ListItem>
  )
}

interface INavListItemWrapperProps {
  /**
   * @default false
   */
  isTooltipDisabled?: boolean
  /**
   * @example 'Home'
   */
  name: string
  /**
   * Optionally pass config props to <Tooltip />
   */
  TooltipProps?: Partial<MuiTooltipProps>
}

/**
 * Helper component which wraps <NavListItem /> in a conditionally
 * rendered <Tooltip /> component
 */
export const NavListItemWrapper: FC<INavListItemWrapperProps> = ({
  isTooltipDisabled = false,
  children,
  name = '',
  TooltipProps = {},
}) => {
  if (isTooltipDisabled) {
    return <>{children}</>
  }

  return (
    <Tooltip title={name} placement='right' key={name} arrow {...TooltipProps}>
      <div>{children}</div>
    </Tooltip>
  )
}

export default NavListItem
